import { ALL_RECORD_TYPE_IDS, ALL_RECORD_TYPES } from 'constants/recordTypes';
import {
  ALL,
  DISABLE_PAGE_LIMIT,
  GenericResource,
  QueryFilterOperators,
} from 'types/api';
import { useSetDocument, usePresenceData } from '@veltdev/react';
import { RecordType } from 'types/apps';
import SidebarInfoSection from 'ui-component/DetailLayout/SidebarComponents/SidebarInfoSection';
import { IconUsers } from '@tabler/icons-react';
import { pluralizeNoun, pluralizeWordOption } from 'utils/functions';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { UserSchema } from 'types/clientV2/users';
import { useGetUsersV2Query } from 'store/slices/clientV2/users';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { InfoSectionItemFormatTypes } from 'ui-component/InfoSectionItem';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { Divider } from '@mui/material';

export interface VeltPresenceSidebarInfoSectionProps<
  T extends GenericResource
> {
  record: T;
  recordType: ALL_RECORD_TYPE_IDS;
}

const VeltPresenceSidebarInfoSection = <T extends GenericResource>({
  record,
  recordType,
}: VeltPresenceSidebarInfoSectionProps<T>) => {
  const { data: currentUser } = useGetUserQuery();

  const recordTypeDefinition = recordType
    ? (ALL_RECORD_TYPES[recordType] as unknown as RecordType<T>)
    : null;

  useSetDocument(record.id, {
    documentName: recordTypeDefinition?.nameGetter?.(record) || '',
  });

  const presenceData = usePresenceData({
    statuses: ['online'],
    documentId: record.id,
  });

  const presenceUserIds = (presenceData.data || [])
    .map((user) => user.userId.split('_')[1])
    .filter((id) => id !== currentUser?.id);
  const presenceUserCount = presenceUserIds.length;

  const { data: presenceUsers, isFetching: isFetchingPresenceUsers } =
    useServerSideQuery<UserSchema, UserSchema>(
      useGetUsersV2Query,
      presenceUserIds.length > 0
        ? {
            pageSize: DISABLE_PAGE_LIMIT,
            schema: [ALL],
            blended: true,
            filters: [
              {
                field: 'id',
                operator: QueryFilterOperators.isAnyOf,
                value: presenceUserIds,
              },
            ],
          }
        : skipToken
    );

  return (
    <>
      <SidebarInfoSection
        caption={`Other Users Viewing this ${recordTypeDefinition?.singularName}`}
        count={presenceUserCount}
        Icon={IconUsers}
        color="primary"
        visible={presenceUserCount > 0}
        collapsedTooltip={`${presenceUserCount} ${pluralizeNoun(
          'user',
          presenceUserCount
        )} ${pluralizeWordOption(
          'is',
          'are',
          presenceUserCount
        )} currently viewing this ${recordTypeDefinition?.singularName}`}
        isLoading={isFetchingPresenceUsers}
        formatType={InfoSectionItemFormatTypes.users}
        usersValue={presenceUsers?.data || []}
      />
      {presenceUserCount > 0 && <Divider flexItem />}
    </>
  );
};

export default VeltPresenceSidebarInfoSection;
